import type { OTCDealData } from '@src/types/OTC';
import {
  isFixedRateLoanDeal,
  isFloatingRateLoanDeal,
  isForwardDeal,
  isOptionDeal,
  isRepoDeal,
} from '@src/types/OTC/otcGuards';
import type { SecuritiesContextProps, Security } from '@talos/kyoko';
import {
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isOTCForwardProductTemplate,
  isOTCOptionProductTemplate,
  isOTCRepoProductTemplate,
} from '@talos/kyoko';

type ProductTemplateContext = Pick<SecuritiesContextProps, 'securitiesBySymbol'>;

type IsProductTemplate = (security: Security) => boolean;

/**
 * Each OTC product type maps a deal guard to the guard that confirms a candidate Security
 * is the matching product template.
 */
const PRODUCT_TYPES: Array<{
  isDeal: (deal: OTCDealData) => boolean;
  isProductTemplate: IsProductTemplate;
}> = [
  {
    isDeal: isOptionDeal,
    isProductTemplate: isOTCOptionProductTemplate,
  },
  {
    isDeal: isForwardDeal,
    isProductTemplate: isOTCForwardProductTemplate,
  },
  {
    isDeal: isFixedRateLoanDeal,
    isProductTemplate: isFixedRateLoanProductTemplate,
  },
  {
    isDeal: isFloatingRateLoanDeal,
    isProductTemplate: isFloatingRateLoanProductTemplate,
  },
  {
    isDeal: isRepoDeal,
    isProductTemplate: isOTCRepoProductTemplate,
  },
];

/**
 * Gets the product template for a deal. Uses TemplateSymbol only - never deal.Symbol.
 * Product template has ProductDetails (QuoteAsset, PaymentCcy, etc.); deal has DealInfo.
 */
export function getProductTemplateForDeal(
  instrumentData: OTCDealData,
  context: ProductTemplateContext
): Security | undefined {
  const templateSymbol =
    'TemplateSymbol' in instrumentData ? (instrumentData as { TemplateSymbol?: unknown }).TemplateSymbol : undefined;

  if (typeof templateSymbol !== 'string' || templateSymbol === '') {
    return undefined;
  }

  const candidate = context.securitiesBySymbol.get(templateSymbol);
  if (!candidate) {
    return undefined;
  }

  for (const { isDeal, isProductTemplate } of PRODUCT_TYPES) {
    if (!isDeal(instrumentData)) {
      continue;
    }
    return isProductTemplate(candidate) ? candidate : undefined;
  }

  return undefined;
}
